import { FC, useState, useEffect } from 'react';
import { KatButton, KatInput } from '@amzn/katal-react';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { AssetState, AssetStatus } from 'src/types';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { useSelection } from 'src/contexts/SelectionContext';
import {
  GAMIFIED_TRAILER_DEFAULT_TITLES,
  GAMIFIED_TRAILER_REVEAL_TEMPLATES,
  GAMIFIED_TRAILER_DEFAULT_TITLE_FALLBACK,
  GAMIFIED_TRAILER_DEFAULT_REVEAL_TEMPLATE,
  GAMIFIED_TRAILER_CONSTRAINTS,
} from 'src/configs/gamifiedTrailerConfig';
import styles from './GamifiedTrailerInput.module.css';

interface GamifiedTrailerInputProps {
  updateAssetState: (state: Partial<AssetState>) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

const {
  OPTION_LABELS,
  MIN_OPTIONS,
  MAX_OPTIONS,
  MAX_QUESTION_LENGTH,
  MAX_OPTION_LENGTH,
  MAX_TITLE_LENGTH,
  MAX_REVEAL_TEXT_LENGTH,
} = GAMIFIED_TRAILER_CONSTRAINTS;

interface QuizOptionEntry {
  id: string;
  text: string;
}

const createOption = (text = ''): QuizOptionEntry => ({ id: crypto.randomUUID(), text });

export const GamifiedTrailerInput: FC<GamifiedTrailerInputProps> = ({
  updateAssetState,
  disabled = false,
  'data-testid': dataTestId = 'gamified-trailer-input',
}) => {
  const { locale } = useSelection();
  const defaultTitle =
    GAMIFIED_TRAILER_DEFAULT_TITLES[locale] || GAMIFIED_TRAILER_DEFAULT_TITLE_FALLBACK;
  const revealTemplate =
    GAMIFIED_TRAILER_REVEAL_TEMPLATES[locale] || GAMIFIED_TRAILER_DEFAULT_REVEAL_TEMPLATE;

  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<QuizOptionEntry[]>([createOption(), createOption()]);
  const [correctIndex, setCorrectIndex] = useState<number>(0);
  const [additionalRevealText, setAdditionalRevealText] = useState('');

  // Reset title when locale changes
  useEffect(() => {
    setTitle('');
  }, [defaultTitle]);

  const { assetStates } = useAssetContext();
  const status = assetStates[AssetType.VIDEO_TRAILER]?.status;
  const isSubmitting =
    status === AssetStatus.TRANSFORMATION_IN_PROGRESS ||
    status === AssetStatus.PENDING_TRANSFORMATION;
  const isDisabled = disabled || isSubmitting;

  const questionError =
    question.length > MAX_QUESTION_LENGTH
      ? `Question is too long. Maximum ${MAX_QUESTION_LENGTH} characters allowed.`
      : undefined;

  const titleError =
    title.length > MAX_TITLE_LENGTH
      ? `Title is too long. Maximum ${MAX_TITLE_LENGTH} characters allowed.`
      : undefined;

  const revealTextError =
    additionalRevealText.length > MAX_REVEAL_TEXT_LENGTH
      ? `Additional text is too long. Maximum ${MAX_REVEAL_TEXT_LENGTH} characters allowed.`
      : undefined;

  const optionErrors = options.map((opt) =>
    opt.text.length > MAX_OPTION_LENGTH
      ? `Option is too long. Maximum ${MAX_OPTION_LENGTH} characters allowed.`
      : undefined,
  );

  const hasValidationErrors =
    !!questionError || !!titleError || !!revealTextError || optionErrors.some(Boolean);

  const isValid =
    question.trim() !== '' &&
    options.length >= MIN_OPTIONS &&
    options.every((o) => o.text.trim() !== '') &&
    correctIndex >= 0 &&
    correctIndex < options.length &&
    !hasValidationErrors;

  const handleAddOption = () => {
    if (options.length < MAX_OPTIONS) {
      setOptions([...options, createOption()]);
    }
  };

  const handleRemoveOption = (idx: number) => {
    if (options.length <= MIN_OPTIONS) return;
    const newOptions = options.filter((_, i) => i !== idx);
    setOptions(newOptions);
    if (correctIndex >= newOptions.length) {
      setCorrectIndex(newOptions.length - 1);
    } else if (correctIndex === idx) {
      setCorrectIndex(0);
    } else if (correctIndex > idx) {
      setCorrectIndex(correctIndex - 1);
    }
  };

  const handleOptionChange = (idx: number, value: string) => {
    const newOptions = [...options];
    newOptions[idx] = { ...newOptions[idx], text: value };
    setOptions(newOptions);
  };

  const handleCancel = () => {
    setTitle('');
    setQuestion('');
    setOptions([createOption(), createOption()]);
    setCorrectIndex(0);
    setAdditionalRevealText('');
    updateAssetState({ status: AssetStatus.PENDING_INPUT });
  };

  // Sets state to PENDING_TRANSFORMATION — the useAssetInput pipeline picks this up
  // and calls handleTransformation via AssetTransformationHandler (no file upload needed).
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    const quizOptions = options.map((opt, idx) => ({
      id: OPTION_LABELS[idx],
      text: `${OPTION_LABELS[idx]}) ${opt.text}`,
      isCorrect: idx === correctIndex,
    }));

    // Build reveal text: base template with answer + optional additional text
    const correctAnswerText = options[correctIndex]?.text || '';
    const baseReveal = revealTemplate.replace('{answer}', () => correctAnswerText);
    const finalRevealText = additionalRevealText.trim()
      ? `${baseReveal}. ${additionalRevealText.trim()}`
      : baseReveal;

    updateAssetState({
      status: AssetStatus.PENDING_TRANSFORMATION,
      mediaText: {
        title: title.trim() || defaultTitle,
        question,
        reveal_text: finalRevealText,
      },
      quizOptions,
    });
  };

  return (
    <form className={styles.container} data-testid={dataTestId} onSubmit={handleSave}>
      <div className={styles.fieldGroup}>
        <KatInput
          label="Title"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder={defaultTitle}
          disabled={isDisabled}
          data-testid={`${dataTestId}-title`}
        />
        {titleError && <span className={styles.validationError}>{titleError}</span>}

        <KatInput
          label="Question *"
          value={question}
          onInput={(e) => setQuestion((e.target as HTMLInputElement).value)}
          placeholder="Enter trivia question"
          disabled={isDisabled}
          data-testid={`${dataTestId}-question`}
        />
        {questionError && <span className={styles.validationError}>{questionError}</span>}

        <label className={styles.optionsLabel}>Options *</label>
        {options.map((opt, idx) => (
          <div key={opt.id}>
            <div className={styles.optionRow}>
              <input
                type="radio"
                name="correctAnswer"
                checked={correctIndex === idx}
                onChange={() => setCorrectIndex(idx)}
                disabled={isDisabled}
                title="Mark as correct answer"
              />
              <span className={styles.optionLabel}>{OPTION_LABELS[idx]})</span>
              <KatInput
                value={opt.text}
                onInput={(e) => handleOptionChange(idx, (e.target as HTMLInputElement).value)}
                placeholder={`Option ${OPTION_LABELS[idx]}`}
                disabled={isDisabled}
                data-testid={`${dataTestId}-option-${idx}`}
              />
              {options.length > MIN_OPTIONS && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveOption(idx)}
                  disabled={isDisabled}
                  title="Remove option"
                >
                  ✕
                </button>
              )}
            </div>
            {optionErrors[idx] && (
              <span className={styles.validationError}>{optionErrors[idx]}</span>
            )}
          </div>
        ))}

        {options.length < MAX_OPTIONS && (
          <button
            type="button"
            className={styles.addOptionButton}
            onClick={handleAddOption}
            disabled={isDisabled}
          >
            + Add option
          </button>
        )}
        <p className={styles.hint}>Select the radio button next to the correct answer</p>

        <div className={styles.revealSection}>
          <KatInput
            label="Reveal Text"
            value={additionalRevealText}
            onInput={(e) => setAdditionalRevealText((e.target as HTMLInputElement).value)}
            placeholder="Add an extra fact or detail to narrate after the reveal"
            disabled={isDisabled}
            data-testid={`${dataTestId}-reveal-text`}
          />
          {revealTextError && <span className={styles.validationError}>{revealTextError}</span>}
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <KatButton
          type="button"
          variant="tertiary"
          onClick={handleCancel}
          disabled={isDisabled}
          data-testid={`${dataTestId}-cancel-button`}
        >
          Cancel
        </KatButton>
        <KatButton
          variant="primary"
          disabled={!isValid || isDisabled}
          data-testid={`${dataTestId}-save-button`}
          type="submit"
        >
          Generate Trailer
        </KatButton>
      </div>
    </form>
  );
};
